const INTAKE_BASE = 'https://bormannbio.com/api/intake/v1/parahealth';
const TOPICS = new Set(['general', 'support', 'returns', 'b2b']);
const failure = code => Object.assign(new Error(code), { code });

export function buildInquiry(data, locale, source, submissionId) {
  const value = key => String(data.get(`contact[${key}]`) || '').trim();
  const topic = value('subject');
  if (!TOPICS.has(topic)) throw failure('error');
  const url = new URL(source);
  const fields = {};
  for (const [key, raw] of data.entries()) {
    const match = /^contact\[([^\]]+)\]$/.exec(key);
    if (!match || ['name', 'email', 'company', 'subject', 'body'].includes(match[1].toLowerCase())) continue;
    if (['__proto__', 'constructor', 'prototype'].includes(match[1])) continue;
    fields[match[1].slice(0, 80)] = String(raw).trim().slice(0, 1000);
  }
  if (Object.keys(fields).length > 20) throw failure('error');
  return { submissionId, locale, sourceUrl: url.origin + url.pathname, topic,
    contact: { name: value('name'), email: value('email'), company: value('company') || value('Company') },
    message: value('body'), fields };
}

export const intakeUrl = payload => `${INTAKE_BASE}/${payload.topic === 'b2b' ? 'sales' : 'support'}`;

export async function deliverInquiry(payload, token, fetcher = fetch) {
  let response;
  try {
    response = await fetcher(intakeUrl(payload), { method: 'POST', credentials: 'omit', redirect: 'error',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': payload.submissionId },
      body: JSON.stringify({ ...payload, turnstileToken: token }), signal: AbortSignal.timeout(20000) });
  } catch { throw failure('uncertain'); }
  if ([400, 403, 413, 422, 429].includes(response.status)) throw failure('error');
  let receipt;
  try { receipt = await response.json(); } catch { throw failure('uncertain'); }
  if (!response.ok || receipt.success !== true || receipt.status !== 'accepted' || receipt.submissionId !== payload.submissionId) throw failure('uncertain');
  return receipt;
}

let botLoader;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!botLoader) botLoader = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    const timer = setTimeout(() => reject(failure('unavailable')), 12000);
    script.onload = () => { clearTimeout(timer); window.turnstile ? resolve(window.turnstile) : reject(failure('unavailable')); };
    script.onerror = () => { clearTimeout(timer); reject(failure('unavailable')); };
    document.head.append(script);
  });
  return botLoader;
}

// Only a hash and submission ID survive reloads. Names, addresses and messages stay in the form.
async function stableId(payload, storageKey) {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(payload)));
  const hash = Array.from(new Uint8Array(bytes), n => n.toString(16).padStart(2, '0')).join('');
  let previous;
  try { previous = JSON.parse(sessionStorage.getItem(storageKey)); } catch { /* Storage can be disabled. */ }
  if (previous?.hash === hash && /^[0-9a-f-]{36}$/i.test(previous.id)) return previous.id;
  const id = crypto.randomUUID();
  try { sessionStorage.setItem(storageKey, JSON.stringify({ hash, id })); } catch { /* In-memory retries still work. */ }
  return id;
}

function connect(form) {
  if (form.dataset.phConnected) return;
  form.dataset.phConnected = 'true';
  const output = form.querySelector('[data-ph-status]');
  const button = form.querySelector('[type="submit"]');
  const holder = form.querySelector('[data-ph-turnstile]');
  const fallback = form.querySelector('[data-ph-email-fallback]');
  const inputs = [...form.querySelectorAll('input, textarea, select')];
  const initialDisabled = inputs.map(input => input.disabled);
  const storageKey = `ph-inquiry:${location.origin}${location.pathname}:${form.id}`;
  let busy = false, token = '', widget, pending, uncertain = false;
  const status = key => { output.textContent = key ? output.dataset[key] : ''; output.hidden = !key; };
  const lock = locked => inputs.forEach((input, i) => { input.disabled = locked || initialDisabled[i]; });
  const updateFallback = () => {
    const sales = form.querySelector('[name="contact[subject]"]')?.value === 'b2b';
    if (fallback) { fallback.href = `mailto:${sales ? 'sales@parahealth.de' : 'support@parahealth.com'}`; fallback.textContent = sales ? 'sales@parahealth.de' : 'support@parahealth.com'; }
  };
  form.addEventListener('change', updateFallback);
  updateFallback();
  form.addEventListener('focusin', async () => {
    try {
      const [response, bot] = await Promise.all([
        fetch(`${INTAKE_BASE}/config`, { credentials: 'omit', redirect: 'error', signal: AbortSignal.timeout(10000) }), loadTurnstile()
      ]);
      if (!response.ok) throw failure('unavailable');
      const config = await response.json();
      if (typeof config.siteKey !== 'string' || !config.siteKey || config.action !== 'contact') throw failure('unavailable');
      widget = bot.render(holder, { sitekey: config.siteKey, action: 'contact', language: form.dataset.locale,
        callback: value => { token = value; button.disabled = busy; },
        'expired-callback': () => { token = ''; button.disabled = true; },
        'error-callback': () => { token = ''; button.disabled = true; status('verification'); }
      });
    } catch { status('unavailable'); }
  }, { once: true });
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy) return;
    if (!token) { status('verification'); return; }
    if (!pending && !form.reportValidity()) return;
    busy = true; button.disabled = true;
    try {
      if (!pending) {
        pending = buildInquiry(new FormData(form), form.dataset.locale, location.href, undefined);
        pending.submissionId = await stableId(pending, storageKey);
      }
      lock(true); status('sending'); form.setAttribute('aria-busy', 'true');
      await deliverInquiry(pending, token);
      pending = undefined; uncertain = false;
      try { sessionStorage.removeItem(storageKey); } catch { /* Optional storage. */ }
      lock(false); form.reset(); updateFallback(); status('received');
    } catch (error) {
      const code = uncertain ? 'uncertain' : (error.code || 'uncertain');
      uncertain = code === 'uncertain';
      if (!uncertain) { pending = undefined; lock(false); }
      status(code);
    } finally {
      busy = false; token = ''; form.removeAttribute('aria-busy');
      if (widget !== undefined) window.turnstile.reset(widget);
    }
  });
}

if (typeof document !== 'undefined') {
  const connectAll = () => document.querySelectorAll('form[data-ph-inquiry]').forEach(connect);
  connectAll();
  document.addEventListener('shopify:section:load', connectAll);
}
